import React, {
  useState,
  ReactNode,
  ReactElement,
  useEffect,
} from 'react';
import { v4 as uuid } from 'uuid';
import {Dialog} from '@shortsworks/ui/components/ui/dialog';

export interface ModalResult<T = any> {
  value: T | undefined;
}

export type ModalProps<T = {}, R = any> = T & {
  modalId: string;
  close: (result?: ModalResult<R>) => void;
};

interface ModalInstance<T = {}, R = any> {
  id: string;
  open: boolean;
  close: (result: ModalResult<R>) => void;
  component: ReactElement<ModalProps<T, R>>;
}

export interface ModalContextType {
  showModal: <T extends {}, R>(Component: React.ComponentType<ModalProps<T, R>>, props?: Omit<T, 'close' | 'modalId'>) => {modalId: string; promise: () => Promise<ModalResult<R>>; };
  closeModal: <T>(id: string, result?: ModalResult<T>) => void;
}

export const ModalContext = React.createContext<ModalContextType | null>(null);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modals, setModals] = useState<ModalInstance<unknown>[]>([]);
  const [ closeModalQueue, setCloseModalQueue ] = useState<Array<{id: string; result: ModalResult<unknown> | undefined}>>([]);

  useEffect(() => {
    const foundIds: string[] = [];
    closeModalQueue.forEach(({id, result}) => {
      if(modals.find(modal => modal.id === id)) {
        foundIds.push(id);
        closeModal(id, result);
      }
    });

    if (foundIds.length > 0) {
      setCloseModalQueue(closeModalQueue.filter(({id}) => !foundIds.includes(id) ));
    }

  }, [modals, closeModalQueue]);

  const showModal =<T extends {}, R>(Component: React.ComponentType<ModalProps<T, R>>, props?: Omit<T, 'close' | 'modalId'>): {
    modalId: string,
    promise: () => Promise<ModalResult<R>>
  } => {
    const id = uuid();
    let internalResolve: any;
    const modalResult = new Promise<ModalResult<R>>((resolve) => {
      internalResolve = resolve;
    });

    const close = (result: ModalResult<R> = { value: undefined }) => {
      setModals(currentModals => currentModals.map(modal => modal.id === id ? { ...modal, open: false } : modal));
      setTimeout(() => {
        setModals(currentModals => currentModals.filter(modal => modal.id !== id));
        internalResolve(result);
      }, 200);
    };

    const modalInstance: ModalInstance<T, R> = {
      id,
      open: true,
      close,
      component: <Component {...props as ModalProps<T, R>} modalId={id} close={close} />,
    };
    setModals(currentModals => [...currentModals, modalInstance]);

    return { modalId: id, promise: () => modalResult };
  };

  const closeModal = <T extends any>(id: string, result?: ModalResult<T> ) => {
    const modal = modals.find(modal => modal.id === id);
    if (modal) {
      const modalResult: ModalResult<T> = result ?? { value: undefined }; // 모달이 사용자에 의해 닫힘
      modal.close(modalResult);
    } else {
      setCloseModalQueue(current => [...current, {id, result}]);
    }
  }

  return (
    <ModalContext.Provider value={{ showModal, closeModal }}>
      {children}
      {modals.map(modal => (
        <Dialog open={modal.open} key={modal.id} onOpenChange={(value: boolean) => !value && modal.close({ value: undefined })}>
          {modal.component}
        </Dialog>
      ))}
    </ModalContext.Provider>
  );
};
